<template>
  <div>
    <div class="tradein-section">
      <div class="tradein-header">
        <div class="tradein-icon">
          <v-icon color="primary" size="42">
            mdi-swap-horizontal-circle-outline
          </v-icon>
        </div>

        <div class="tradein-header-text">
          <div class="headline-s">Trade-in เก่าแลกใหม่</div>
          <div class="body-s desc">
            เปลี่ยนเครื่องเก่าที่ไม่ได้ใช้ให้เป็นส่วนลด สำหรับซื้อ
            iPhone เครื่องใหม่ พร้อมรับส่วนลด On-Top
            เพิ่มเติมจากโปรโมชันปกติ
          </div>
        </div>
      </div>

      <BaseTradeInOptionGroup
        v-if="isPreTradeInOptionsEnabled"
        v-model="form.tradein_type"
        heading="ฉันประเมินเครื่องมาแล้ว"
        :options="preTradeInOptions"
      />

      <BaseTradeInVoucherSection
        v-if="isPreTradeInSelected"
        :value="displayVouchers"
        :type="form.tradein_type"
        :is-loading="isTechswopValidateLoading"
        :has-required-error="showVoucherRequiredError"
        @input="onVouchersInput"
        @verify="onVerifyVoucher"
        @remove="onRemove"
      />

      <BaseTradeInOptionGroup
        v-if="isInterestTradeInOptionsEnabled"
        v-model="form.tradein_type"
        heading="ฉันยังไม่ประเมินเครื่อง"
        :options="interestTradeInOptions"
      />

      <BaseTradeInVoucherMismatchModal
        v-model="showMismatchModal"
        :type="form.tradein_type"
        :vouchers="mismatchedVouchers"
        :current-branch-name="currentBranchName"
        :total-price="orderSummary?.base_amount ?? 0"
        @confirm="onSwitchBranchConfirm"
      />
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { validationMixin } from 'vuelidate'
import { requiredIf } from 'vuelidate/lib/validators'
import BaseTradeInOptionGroup from './BaseTradeInOptionGroup.vue'
import BaseTradeInVoucherSection from './BaseTradeInVoucherSection.vue'
import BaseTradeInVoucherMismatchModal from './BaseTradeInVoucherMismatchModal.vue'
import {
  PRE_TRADE_IN_OPTIONS,
  INTEREST_TRADE_IN_OPTIONS,
  TRADEIN_TYPE,
} from '@/config/order'

export default {
  name: 'BaseTradeInSection',

  mixins: [validationMixin],

  components: {
    BaseTradeInOptionGroup,
    BaseTradeInVoucherSection,
    BaseTradeInVoucherMismatchModal,
  },

  props: {
    options: {
      type: Array,
      default: () => Object.values(TRADEIN_TYPE),
    },
  },

  validations: {
    form: {
      vouchers: {
        required: requiredIf(function() {
          return this.isPreTradeInSelected
        }),
        $each: {
          code: {
            required: requiredIf(function() {
              return this.isPreTradeInSelected
            }),
            format(value) {
              return /^TRD[A-Za-z0-9-]+$/i.test(value)
            },
            unique(value) {
              return (
                this.form.vouchers.filter(
                  voucher => voucher.code === value,
                ).length === 1
              )
            },
          },
        },
      },
    },
  },

  computed: {
    preTradeInOptions() {
      return PRE_TRADE_IN_OPTIONS.map(option => ({
        ...option,
        enabled: this.options.includes(option.value),
      }))
    },

    isPreTradeInOptionsEnabled() {
      return this.preTradeInOptions.some(option => option.enabled)
    },

    interestTradeInOptions() {
      return INTEREST_TRADE_IN_OPTIONS.map(option => ({
        ...option,
        enabled: this.options.includes(option.value),
      }))
    },

    isInterestTradeInOptionsEnabled() {
      return this.interestTradeInOptions.some(
        option => option.enabled,
      )
    },

    ...mapState({
      techswopValidate: state =>
        state.paymentType.techswop.validate?.data,
      isTechswopValidateLoading: state =>
        state.paymentType.techswop.validate.isLoading,
      techswopSwitchBranch: state =>
        state.paymentType.techswop.switchBranch?.data,
      techswopConfirm: state => state.paymentType.techswop.confirm,
      currentBranchName: state =>
        state.order.data?.receive_branch_name ?? null,
    }),

    orderUuid() {
      return this.$route.params.uid || this.$route.params.orderId
    },

    orderSummary() {
      return this.techswopValidate?.order_summary ?? null
    },

    mismatchedVouchers() {
      return this.form.vouchers.filter(
        voucher =>
          voucher.valid || voucher.reason === 'branch_mismatch',
      )
    },

    isPreTradeInSelected() {
      return this.preTradeInOptions.some(
        option => option.value === this.form.tradein_type,
      )
    },

    showVoucherRequiredError() {
      return (
        this.$v.form.vouchers.$dirty &&
        !this.$v.form.vouchers.required
      )
    },

    displayVouchers() {
      return this.form.vouchers.map((voucher, index) => {
        const field = this.$v.form.vouchers.$each[index]?.code

        if (!field || !field.$error) return { ...voucher }

        const message = !field.required
          ? 'กรุณากรอกโค้ด TRD'
          : !field.format
          ? 'รูปแบบโค้ด TRD ไม่ถูกต้อง'
          : !field.unique
          ? 'โค้ด TRD นี้ซ้ำกับรายการอื่น'
          : voucher.message

        return { ...voucher, message }
      })
    },
  },

  watch: {
    'form.tradein_type'(newValue, oldValue) {
      if (
        this.isPreTradeInSelected &&
        oldValue &&
        newValue &&
        newValue !== oldValue
      ) {
        this.onChangePreTradeInType()
      } else {
        this.handleResetTradeInSection()
        this.form.vouchers = this.form.vouchers.map(voucher => ({
          code: voucher.code,
        }))
      }
    },
  },
  mounted() {
    this.handleResetTradeInSection()
  },

  data() {
    return {
      showMismatchModal: false,

      // form
      form: {
        tradein_type: null,
        vouchers: [],
      },
    }
  },

  methods: {
    onChangePreTradeInType() {
      // Reset validation state when a voucher is removed
      this.handleResetTradeInSection()
      this.form.vouchers = this.form.vouchers.map(voucher => ({
        code: voucher.code,
      }))

      // Validation again after removing a voucher if the form.vouchers is not empty
      const hasVouchers = this.form.vouchers.length > 0

      // If there are vouchers and none of them are invalid, trigger the verification process
      if (hasVouchers) {
        this.onVerifyVoucher()
      }
    },

    onRemove() {
      // Reset validation state when a voucher is removed
      this.handleResetTradeInSection()

      this.form.vouchers = this.form.vouchers.map(voucher => ({
        code: voucher.code,
      }))

      // Validation again after removing a voucher if the form.vouchers is not empty
      const hasVouchers = this.form.vouchers.length > 0

      // If there are vouchers and none of them are invalid, trigger the verification process
      if (hasVouchers) {
        this.onVerifyVoucher()
      }
    },

    onVouchersInput(vouchers) {
      this.$v.$reset()

      // Reset messages for each voucher when input changes
      this.form.vouchers = vouchers.map(voucher => ({
        ...voucher,
        message: null,
      }))
    },

    onVerifyVoucher() {
      this.$v.$touch()

      if (this.$v.form.$invalid) {
        return
      }

      this.handleVoucherValidation()
    },

    async handleVoucherValidation() {
      try {
        await this.$store.dispatch('paymentType/validateTechswop', {
          order_uuid: this.orderUuid,
          tradein_type: this.form.tradein_type,
          vouchers: this.form.vouchers.map(voucher => voucher.code),
        })

        const vouchers = this.techswopValidate?.vouchers

        if (!vouchers) return

        this.form.vouchers = this.form.vouchers.map(
          (voucher, index) => ({
            ...voucher,
            ...vouchers[index],
          }),
        )

        // Check if any voucher has an invalid and reason is 'branch_mismatch'
        const hasBranchMismatch =
          this.techswopValidate?.switchable_branches.length > 0

        if (hasBranchMismatch) {
          this.showMismatchModal = true
          return
        }

        // For required change payment gateway, emit event to parent component
        if (this.techswopValidate?.require_payment_change) {
          this.handleShowChangePaymentMethodToast(
            this.techswopValidate?.payment_warning_message,
          )
        }
      } catch (error) {
        // Handle error
      }
    },

    async onSwitchBranchConfirm({ branch }) {
      try {
        await this.$store.dispatch(
          'paymentType/switchTechswopBranch',
          {
            order_uuid: this.orderUuid,
            tradein_type: this.form.tradein_type,
            vouchers: this.form.vouchers.map(voucher => voucher.code),
            branch,
          },
        )

        const vouchers = this.techswopSwitchBranch?.vouchers

        if (vouchers) {
          this.form.vouchers = this.form.vouchers.map(
            (voucher, index) => ({
              ...voucher,
              ...vouchers[index],
            }),
          )
        }

        this.showMismatchModal = false

        const branchName =
          vouchers.filter(voucher => voucher.valid)[0]?.branch_name ??
          null

        if (branchName)
          this.$store.commit('order/setReceiveBranchName', branchName)

        if (this.techswopSwitchBranch?.require_payment_change) {
          this.handleShowChangePaymentMethodToast(
            this.techswopSwitchBranch?.payment_warning_message,
          )
        }
      } catch (error) {
        // Handle error
      }
    },

    async handleConfirmTechswop() {
      try {
        if (!this.form.tradein_type) {
          this.$emit('skip')
          return
        }

        this.$v.form.vouchers.$touch()

        if (this.$v.form.vouchers.$invalid) {
          return
        }

        const payload = {
          order_uuid: this.orderUuid,
          tradein_type: this.form.tradein_type,
        }

        if (this.form.tradein_type !== TRADEIN_TYPE.INTEREST) {
          payload.vouchers = this.form.vouchers.map(
            voucher => voucher.code,
          )
        }

        await this.$store.dispatch(
          'paymentType/confirmTechswop',
          payload,
        )

        if (!this.techswopConfirm.success) return

        this.$emit('success')
      } catch (error) {
        // Handle error
      }
    },

    handleShowChangePaymentMethodToast(message) {
      if (!message) return

      this.$toast.error(message, {
        position: 'top',
        duration: 10000,
      })

      // Scroll to paymentMethodsId element after the DOM has updated and a short delay to ensure the element is rendered
      setTimeout(() => {
        this.$nextTick(() => {
          const paymentMethodsElement = document.getElementById(
            'paymentMethodsId',
          )

          if (paymentMethodsElement) {
            paymentMethodsElement.scrollIntoView({
              behavior: 'smooth',
              block: 'start',
            })
          }
        })
      }, 250)
    },

    handleResetTradeInSection() {
      this.$store.commit('paymentType/setValidateTechswopReset')
      this.$store.commit('paymentType/setSwitchTechswopBranchReset')
      this.$store.commit('paymentType/setConfirmTechswopReset')
      this.$store.commit(
        'paymentType/setChangeTradeInPaymentMethodReset',
      )
    },
  },
}
</script>

<style lang="stylus" scoped>
.-is-bnn{
	.tradein-section {
		padding: 24px;
		background-color: #fff;
		border-radius: 8px;
	}
}

.tradein-section {
    display: flex;
    flex-direction: column;
    gap: 24px;

    padding: 16px;
    background-color: #F9F9F9;
    border-radius: 8px;
}

.tradein-header {
    display: flex;
    align-items: flex-start;
    gap: 16px;
}

.tradein-icon {
    flex-shrink: 0;
}

.tradein-header-text {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.desc {
    color: rgba(#000000, 0.45);
}
</style>
